import React, { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { useMessagesStore } from '../store/messagesStore'
import { useAuthStore } from '../store/authStore'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../components/ui/SafeIcon'

const { FiMessageSquare, FiSend, FiSearch, FiClock } = FiIcons

function MessagesPage() {
  const { t } = useTranslation()
  const location = useLocation()
  const { user } = useAuthStore()
  const { threads, getThreadsByUser, sendMessage, markThreadAsRead } = useMessagesStore()

  const [selectedThread, setSelectedThread] = useState(null)
  const [newMessage, setNewMessage] = useState('')
  const [searchTerm, setSearchTerm] = useState('')

  const userThreads = getThreadsByUser(user?.email)
  const filteredThreads = userThreads.filter(thread =>
    thread.supplierName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    thread.messages.some(msg => msg.text.toLowerCase().includes(searchTerm.toLowerCase()))
  )

  // Auto-select thread if coming from product page or select first thread
  useEffect(() => {
    const threadId = location.state?.threadId
    if (threadId) {
      const thread = threads.find(t => t.id === threadId)
      if (thread) {
        setSelectedThread(thread)
        markThreadAsRead(threadId)
      }
    } else if (!selectedThread && userThreads.length > 0) {
      setSelectedThread(userThreads[0])
      markThreadAsRead(userThreads[0].id)
    }
  }, [location.state, threads, userThreads, selectedThread, markThreadAsRead])

  useEffect(() => {
    if (selectedThread) {
      markThreadAsRead(selectedThread.id)
    }
  }, [selectedThread, markThreadAsRead])

  const handleSendMessage = () => {
    if (newMessage.trim() && selectedThread) {
      sendMessage(selectedThread.id, newMessage.trim())
      setNewMessage('')
    }
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  const formatTime = (timestamp) => {
    const date = new Date(timestamp)
    const now = new Date()
    const diff = now - date

    if (diff < 60000) return 'Jetzt'
    if (diff < 3600000) return `${Math.floor(diff / 60000)}min`
    if (diff < 86400000) return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
    return date.toLocaleDateString('de-DE')
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-bold mb-4">Bitte anmelden</h2>
          <p className="text-gray-600">Sie müssen angemeldet sein, um Nachrichten zu sehen.</p>
        </div>
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <title>Nachrichten | 1688Euro.com</title>
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="flex h-[600px]">
              {/* Threads Sidebar */}
              <div className="w-80 border-r border-gray-200 flex flex-col">
                <div className="p-4 border-b border-gray-200">
                  <h2 className="text-xl font-bold mb-4">Nachrichten</h2>
                  <div className="relative">
                    <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Nachrichten durchsuchen..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto">
                  {filteredThreads.length === 0 ? (
                    <div className="p-4 text-center text-gray-500">
                      {searchTerm ? 'Keine passenden Nachrichten gefunden' : 'Keine Nachrichten'}
                    </div>
                  ) : (
                    filteredThreads.map((thread) => (
                      <div
                        key={thread.id}
                        className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 ${
                          selectedThread?.id === thread.id ? 'bg-blue-50' : ''
                        }`}
                        onClick={() => setSelectedThread(thread)}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="font-bold text-gray-900">{thread.supplierName}</h3>
                          {thread.unreadCount > 0 && (
                            <span className="bg-red-500 text-white text-xs rounded-full px-2 py-1">
                              {thread.unreadCount}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 line-clamp-2 mb-2">
                          {thread.messages[thread.messages.length - 1]?.text}
                        </p>
                        <div className="flex items-center text-xs text-gray-400">
                          <SafeIcon icon={FiClock} className="w-3 h-3 mr-1" />
                          {formatTime(thread.messages[thread.messages.length - 1]?.timestamp)}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Chat Area */}
              <div className="flex-1 flex flex-col">
                {selectedThread ? (
                  <>
                    <div className="p-4 border-b border-gray-200">
                      <h3 className="font-bold text-lg">{selectedThread.supplierName}</h3>
                      <p className="text-sm text-gray-600">Lieferant</p>
                    </div>

                    <div className="flex-1 overflow-y-auto p-4 space-y-4">
                      {selectedThread.messages.map((message) => (
                        <div
                          key={message.id}
                          className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                              message.sender === 'user'
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-100 text-gray-900'
                            }`}
                          >
                            <p className="text-sm">{message.text}</p>
                            <div
                              className={`text-xs mt-1 ${
                                message.sender === 'user' ? 'text-blue-200' : 'text-gray-400'
                              }`}
                            >
                              {formatTime(message.timestamp)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="p-4 border-t border-gray-200">
                      <div className="flex space-x-2">
                        <input
                          type="text"
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          onKeyPress={handleKeyPress}
                          placeholder="Nachricht eingeben..."
                          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                          onClick={handleSendMessage}
                          disabled={!newMessage.trim()}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <SafeIcon icon={FiSend} className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="flex-1 flex items-center justify-center text-gray-500">
                    <div className="text-center">
                      <SafeIcon icon={FiMessageSquare} className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                      <p>Wählen Sie eine Konversation aus, um Nachrichten zu sehen</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default MessagesPage